/**
 * Recurring billing periods for arrangements such as rent.
 *
 * Periods are counted from a fixed first-period start (the anchor) so a 31st start
 * never drifts: period k runs from anchor + k×n months to the day before period k+1.
 * Invoices are raised in advance, from DUE_LEAD_DAYS before a period starts.
 */
import { addDays, addMonths, format, parseISO } from 'date-fns';

export type BillingFrequency = 'none' | 'monthly' | 'quarterly' | 'six_monthly' | 'annually';

export const BILLING_FREQUENCY_LABELS: Record<BillingFrequency, string> = {
  none: 'As needed',
  monthly: 'Monthly',
  quarterly: 'Every 3 months',
  six_monthly: 'Every 6 months',
  annually: 'Yearly',
};

const FREQUENCY_MONTHS: Record<Exclude<BillingFrequency, 'none'>, number> = {
  monthly: 1,
  quarterly: 3,
  six_monthly: 6,
  annually: 12,
};

/** A recurring invoice becomes due this many days before its period starts. */
export const DUE_LEAD_DAYS = 7;

export type BillingPeriod = {
  index: number;
  /** yyyy-MM-dd */
  start: string;
  /** yyyy-MM-dd, inclusive */
  end: string;
};

export type RecurringStatus = 'due' | 'upcoming' | 'ended';

const BILLING_FREQUENCIES = new Set<BillingFrequency>(['none', 'monthly', 'quarterly', 'six_monthly', 'annually']);

export function normaliseFrequency(value: unknown): BillingFrequency {
  return BILLING_FREQUENCIES.has(value as BillingFrequency) ? value as BillingFrequency : 'none';
}

function isoDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** Period `index` (0-based) of a schedule starting on `anchor`. */
export function billingPeriod(anchor: string, frequency: BillingFrequency, index: number): BillingPeriod {
  if (frequency === 'none') throw new Error('This arrangement is not recurring.');
  const months = FREQUENCY_MONTHS[frequency];
  const base = parseISO(anchor);
  const start = addMonths(base, index * months);
  const nextStart = addMonths(base, (index + 1) * months);
  return { index, start: isoDate(start), end: isoDate(addDays(nextStart, -1)) };
}

/** e.g. "1 Nov 2026 to 30 Nov 2026" */
export function periodLabel(period: Pick<BillingPeriod, 'start' | 'end'>): string {
  return `${format(parseISO(period.start), 'd MMM yyyy')} to ${format(parseISO(period.end), 'd MMM yyyy')}`;
}

/** Whether the next period is ready to invoice, still to come, or past the end date. */
export function recurringStatus(args: {
  nextPeriodStart: string;
  endsOn?: string | null;
  today: string;
}): RecurringStatus {
  if (args.endsOn && args.nextPeriodStart > args.endsOn) return 'ended';
  const dueFrom = isoDate(addDays(parseISO(args.nextPeriodStart), -DUE_LEAD_DAYS));
  return args.today >= dueFrom ? 'due' : 'upcoming';
}

export function todayIso(): string {
  return isoDate(new Date());
}
